import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from './client'
import type {
  CameraForwardingSettingsBody,
  CredentialOrder,
  DeviceUpdateBody,
  ForwardingDefaults,
  EventsParams,
  Group,
  LocationNode,
  LocationPayload,
  ZonePayload,
  DoorCodeBody,
  DoorCodeSeriesBody,
  DoorCodeSeriesPatchBody,
  MappingBoard,
  MappingDecideBody,
  MappingItem,
  MappingSplitOnNode,
  MergeDuplicatesBody,
  ScheduleKind,
  SystemProvider,
  WebhookSubscriptionBody,
} from './types'
import { useAuthStore } from '../stores/auth'
import { isExpired } from '../lib/jwt'
import { credentialBundleProviders } from '../components/features/MappingCreateForm'

/** Scope all query keys to the active customer so switching customers refetches. */
function useScope(): string {
  return useAuthStore((s) => s.customer?.unique_id ?? 'none')
}

function useEnabled(): boolean {
  return useAuthStore((s) => !isExpired(s.accessToken))
}

export function useSystems() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'systems'], queryFn: api.systems, enabled: useEnabled() })
}

export function useSyncAllSystems() {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (provider: SystemProvider) => api.syncAllSystems(provider),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [scope, 'systems'] })
      void queryClient.invalidateQueries({ queryKey: [scope, 'sync-runs'] })
    },
  })
}

export function usePersons(q?: string, location?: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'persons', q ?? '', location ?? ''],
    queryFn: () => api.persons(q, location),
    enabled: useEnabled(),
  })
}

export function usePerson(uid: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'persons', 'detail', uid],
    queryFn: () => api.person(uid),
    enabled: useEnabled() && uid.length > 0,
  })
}

/** Merge a person into another person. Invalidates the whole customer scope
 *  and the caller is expected to navigate to the target. */
export function useMergePerson() {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ uid, target }: { uid: string; target: string }) => api.mergePerson(uid, target),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [scope] })
    },
  })
}

/** Detected duplicate person groups (GET /persons/duplicates). */
export function usePersonDuplicates() {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'persons', 'duplicates'],
    queryFn: api.personDuplicates,
    enabled: useEnabled(),
  })
}

/** The duplicate group a person belongs to (null when none). */
export function usePersonDuplicateGroup(uid: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'persons', 'detail', uid, 'duplicates'],
    queryFn: () => api.personDuplicateGroup(uid),
    enabled: useEnabled() && uid.length > 0,
  })
}

/** A 503 `busy` answer: the server is busy with the same persons, retry later. */
export function isBusy(error: unknown): boolean {
  return error instanceof ApiError && error.status === 503
}

/** Merge preview (operator; a rolled-back simulation). The previous result
 *  stays on screen while a survivor/selection change refetches. */
export function useMergePreview(
  persons: string[],
  survivor: string | undefined,
  groups: string[] | undefined,
  enabled = true,
) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'merge-preview', persons.join(','), survivor ?? '', groups === undefined ? null : groups.join(',')],
    queryFn: () => api.mergePreview(persons, survivor, groups),
    enabled: useEnabled() && enabled && persons.length >= 2,
    placeholderData: keepPreviousData,
    // A 400 (bad set) or 403 (viewer) will not heal on retry; a 503 `busy`
    // (another merge holds the locks) is retried once after its Retry-After.
    retry: (failureCount, error) => isBusy(error) && failureCount < 1,
    retryDelay: (_attempt, error) =>
      error instanceof ApiError && error.retryAfter !== null ? Math.min(error.retryAfter, 10) * 1000 : 1000,
  })
}

/** Execute a guided duplicates merge. The caller disables the preview query
 *  first (the merged-away persons no longer exist) and then invalidates. */
export function useMergeDuplicates() {
  return useMutation({ mutationFn: (body: MergeDuplicatesBody) => api.mergeDuplicates(body) })
}

/** Post-merge verification, polled every `pollMs` until the vendor sync
 *  settles -- or, while `pollUntil` (epoch ms) is in the future, until it is
 *  ok (a retry re-opens the window). */
export function useMergeVerification(survivor: string, verification: string, pollMs: number, pollUntil = 0) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'merge-verification', survivor, verification],
    queryFn: () => api.mergeVerification(survivor, verification),
    enabled: useEnabled() && survivor.length > 0 && verification.length > 0,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return pollMs
      if (!data.settled) return pollMs
      if (!data.ok && Date.now() < pollUntil) return pollMs
      return false
    },
  })
}

export function useGroups(q?: string) {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'groups', q ?? ''], queryFn: () => api.groups(q), enabled: useEnabled() })
}

export function useGroupsTree() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'groups', 'tree'], queryFn: api.groupsTree, enabled: useEnabled() })
}

export function useGroupOccupancies(uid: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'groups', 'occupancies', uid],
    queryFn: () => api.groupOccupancies(uid),
    enabled: useEnabled() && uid.length > 0,
  })
}

export function useGroup(uid: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'groups', 'detail', uid],
    queryFn: () => api.group(uid),
    enabled: useEnabled() && uid.length > 0,
  })
}

/** Merge a directory group into another directory group. Invalidates the whole
 *  customer scope; caller navigates to the target. */
export function useMergeGroup() {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ uid, target }: { uid: string; target: string }) => api.mergeGroup(uid, target),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [scope] })
    },
  })
}

export function useLocationTree() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'locations', 'tree'], queryFn: api.locationTree, enabled: useEnabled() })
}

export function useDoors() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'doors'], queryFn: api.doors, enabled: useEnabled() })
}

export function useZones() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'zones'], queryFn: api.zones, enabled: useEnabled() })
}

export function useCreateZone() {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: ZonePayload) => api.createZone(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [scope, 'zones'] }),
  })
}

export function useUpdateZone() {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ uid, body }: { uid: string; body: ZonePayload }) => api.updateZone(uid, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [scope, 'zones'] }),
  })
}

/** Soft delete — the zone lands in Trash. */
export function useDeleteZone() {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (uid: string) => api.deleteZone(uid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [scope, 'zones'] })
      void queryClient.invalidateQueries({ queryKey: [scope, 'trash'] })
    },
  })
}

// --- Wisenet WAVE event forwarding ---

export function useCameraSettings(deviceUid: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'wisenet', 'camera-settings', deviceUid],
    queryFn: () => api.cameraSettings(deviceUid),
    enabled: useEnabled() && !!deviceUid,
  })
}

export function useUpdateCameraSettings(deviceUid: string) {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CameraForwardingSettingsBody) => api.updateCameraSettings(deviceUid, body),
    onSuccess: (data) => queryClient.setQueryData([scope, 'wisenet', 'camera-settings', deviceUid], data),
  })
}

export function useForwardingDefaults(systemUid: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'wisenet', 'forwarding', systemUid],
    queryFn: () => api.forwardingDefaults(systemUid),
    enabled: useEnabled() && !!systemUid,
  })
}

export function useUpdateForwardingDefaults(systemUid: string) {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: ForwardingDefaults) => api.updateForwardingDefaults(systemUid, body),
    onSuccess: (data) => {
      queryClient.setQueryData([scope, 'wisenet', 'forwarding', systemUid], data)
      // Cameras that inherit show the system default — refetch them.
      void queryClient.invalidateQueries({ queryKey: [scope, 'wisenet', 'camera-settings'] })
    },
  })
}

/** Camera bookmarks made for one event; empty for most events. */
export function useEventBookmarks(eventUid: string | null) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'wisenet', 'event-bookmarks', eventUid],
    queryFn: () => api.eventBookmarks(eventUid as string),
    enabled: useEnabled() && !!eventUid,
  })
}

export function useLocation(uid: string | null) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'locations', 'detail', uid ?? ''],
    queryFn: () => api.location(uid as string),
    enabled: useEnabled() && !!uid,
  })
}

/** The location card's "Credentials": the occupants of this location (and
 *  its subtree), their credentials, and any other credential granted here
 *  within the last `days` (issues #51/#54). */
export function useLocationCredentialUsage(uid: string | null, days: number) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'locations', 'credential-usage', uid ?? '', days],
    queryFn: () => api.locationCredentialUsage(uid as string, days),
    enabled: useEnabled() && !!uid,
  })
}

/** Create a canonical location; invalidates the location keys so the tree
 *  (and any detail caches) refetch with the new node. */
export function useCreateLocation() {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: LocationPayload) => api.createLocation(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [scope, 'locations'] }),
  })
}

/** Door codes, optionally filtered: `series='none'` narrows to static
 *  (series-less) codes, a series uid to that series' period codes. The
 *  unfiltered list keeps the historic 2-part key the ws channel patches
 *  surgically; filtered variants are invalidated (coalesced) instead. */
export function useDoorCodes(series?: string, location?: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'door-codes', series ?? '', location ?? ''],
    queryFn: () => api.doorCodes(series, location),
    enabled: useEnabled(),
  })
}

export function useCreateDoorCode() {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: DoorCodeBody) => api.createDoorCode(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [scope, 'door-codes'] }),
  })
}

export function useUpdateDoorCode() {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ uid, body }: { uid: string; body: DoorCodeBody }) => api.updateDoorCode(uid, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [scope, 'door-codes'] }),
  })
}

/** Rotating door-code series (access.rotation). */
export function useDoorCodeSeries() {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'door-code-series'],
    queryFn: api.doorCodeSeries,
    enabled: useEnabled(),
  })
}

/** Series mutations invalidate the door-code lists too — a create/patch
 *  rolls on commit (period codes appear), a delete turns surviving period
 *  codes into static ones. */
function useInvalidateSeries() {
  const scope = useScope()
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: [scope, 'door-code-series'] })
    void queryClient.invalidateQueries({ queryKey: [scope, 'door-codes'] })
  }
}

export function useCreateDoorCodeSeries() {
  const invalidate = useInvalidateSeries()
  return useMutation({
    mutationFn: (body: DoorCodeSeriesBody) => api.createDoorCodeSeries(body),
    onSuccess: invalidate,
  })
}

export function useUpdateDoorCodeSeries() {
  const invalidate = useInvalidateSeries()
  return useMutation({
    mutationFn: ({ uid, body }: { uid: string; body: DoorCodeSeriesPatchBody }) =>
      api.updateDoorCodeSeries(uid, body),
    onSuccess: invalidate,
  })
}

export function useDeleteDoorCodeSeries() {
  const invalidate = useInvalidateSeries()
  return useMutation({
    mutationFn: (uid: string) => api.deleteDoorCodeSeries(uid),
    onSuccess: invalidate,
  })
}

/** Manual roll ("Roll now"): the response is the roll summary, rendered
 *  transiently on the series row. */
export function useRollDoorCodeSeries() {
  const invalidate = useInvalidateSeries()
  return useMutation({
    mutationFn: (uid: string) => api.rollDoorCodeSeries(uid),
    onSuccess: invalidate,
  })
}

export function useAccessGroups() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'access-groups'], queryFn: api.accessGroups, enabled: useEnabled() })
}

/** Merge an access group into another access group. Invalidates the whole
 *  customer scope; caller navigates to the target. */
export function useMergeAccessGroup() {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ uid, target }: { uid: string; target: string }) => api.mergeAccessGroup(uid, target),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [scope] })
    },
  })
}

export function useAccessGroupMembers(uid: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'access-groups', uid, 'members'],
    queryFn: () => api.accessGroupMembers(uid),
    enabled: useEnabled() && uid.length > 0,
  })
}

/** Directory-group grants for a single access group. */
export function useDirectoryGrants(uid: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'access-groups', uid, 'directory-grants'],
    queryFn: () => api.directoryGrants(uid),
    enabled: useEnabled() && uid.length > 0,
  })
}

/** Directory-grant rows for every access group, keyed by access-group uid.
 *  The directory-group Access card needs the grant uids (for DELETE), which
 *  the effective-access projection does not expose. */
export function useAllDirectoryGrants() {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'directory-grants'],
    queryFn: async () => {
      const groups = await api.accessGroups()
      return Promise.all(
        groups.map(async (g) => ({ access_group: g.unique_id, grants: await api.directoryGrants(g.unique_id) })),
      )
    },
    enabled: useEnabled(),
  })
}

export function usePersonEffectiveAccess(uid: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'effective-access', 'persons', uid],
    queryFn: () => api.personEffectiveAccess(uid),
    enabled: useEnabled() && uid.length > 0,
  })
}

export function useAccessGroupEffectiveDoors(uid: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'effective-access', 'access-groups', uid],
    queryFn: () => api.accessGroupEffectiveDoors(uid),
    enabled: useEnabled() && uid.length > 0,
  })
}

export function useDirectoryGroupEffectiveAccess(uid: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'effective-access', 'directory-groups', uid],
    queryFn: () => api.directoryGroupEffectiveAccess(uid),
    enabled: useEnabled() && uid.length > 0,
  })
}

export function useDoorPolicies() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'door-policies'], queryFn: api.doorPolicies, enabled: useEnabled() })
}

export function useMemberRules() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'member-rules'], queryFn: api.memberRules, enabled: useEnabled() })
}

/** Unfiltered keeps the exact [scope, 'schedules'] key the WS live-updater
 *  upserts into (useConsoleLive.ts); a kind filter adds a key segment and the
 *  live-updater invalidates those instead (it can't upsert kind-safely). */
export function useSchedules(kind?: ScheduleKind) {
  const scope = useScope()
  return useQuery({
    queryKey: kind ? [scope, 'schedules', kind] : [scope, 'schedules'],
    queryFn: () => api.schedules(kind),
    enabled: useEnabled(),
  })
}

export function useInfiniteEvents(params: EventsParams = {}) {
  const scope = useScope()
  return useInfiniteQuery({
    queryKey: [scope, 'events', 'infinite', params],
    queryFn: ({ pageParam }) => api.events({ ...params, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: useEnabled(),
  })
}

export function useEvents(params: EventsParams = {}) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'events', params],
    queryFn: () => api.events(params),
    enabled: useEnabled(),
  })
}

export function useMatchSuggestions(status?: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'match-suggestions', status ?? ''],
    queryFn: () => api.matchSuggestions(status),
    enabled: useEnabled(),
  })
}

export function useUnknownCredentials(status?: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'unknown-credentials', status ?? ''],
    queryFn: () => api.unknownCredentials(status),
    enabled: useEnabled(),
  })
}

export function useCommands() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'commands'], queryFn: api.commands, enabled: useEnabled() })
}

/** Unfiltered (no `order`) keeps the historic 2-part key the ws channel
 *  patches surgically (useConsoleLive.ts); an `order` adds a key segment and
 *  the live-updater invalidates that variant instead (a change can move a
 *  row's position in the sort, which can't be patched surgically). */
export function useCredentials(order?: CredentialOrder) {
  const scope = useScope()
  return useQuery({
    queryKey: order ? [scope, 'credentials', order] : [scope, 'credentials'],
    queryFn: () => api.credentials(order),
    enabled: useEnabled(),
  })
}

export function useCredential(uid: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'credentials', 'detail', uid],
    queryFn: () => api.credential(uid),
    enabled: useEnabled() && uid.length > 0,
  })
}

export function useOperators() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'operators'], queryFn: api.operators, enabled: useEnabled() })
}

// --- Outgoing webhooks ---

export function useWebhooks() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'webhooks'], queryFn: api.webhooks, enabled: useEnabled() })
}

/** The global event-type taxonomy (webhook builder pick-list). Unscoped
 *  server-side, but keyed per-scope here like every other query. */
export function useEventTypes() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'event-types'], queryFn: api.eventTypes, enabled: useEnabled() })
}

/** The 50 most recent delivery rows for one subscription. */
export function useWebhookDeliveries(uid: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'webhooks', 'deliveries', uid],
    queryFn: () => api.webhookDeliveries(uid),
    enabled: useEnabled() && uid.length > 0,
  })
}

function useInvalidateWebhooks() {
  const scope = useScope()
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: [scope, 'webhooks'] })
}

export function useCreateWebhook() {
  const invalidate = useInvalidateWebhooks()
  return useMutation({
    mutationFn: (body: WebhookSubscriptionBody) => api.createWebhook(body),
    onSuccess: invalidate,
  })
}

export function useUpdateWebhook() {
  const invalidate = useInvalidateWebhooks()
  return useMutation({
    mutationFn: ({ uid, body }: { uid: string; body: WebhookSubscriptionBody }) => api.updateWebhook(uid, body),
    onSuccess: invalidate,
  })
}

export function useDeleteWebhook() {
  const invalidate = useInvalidateWebhooks()
  return useMutation({
    mutationFn: (uid: string) => api.deleteWebhook(uid),
    onSuccess: invalidate,
  })
}

export function useAvailableSites(uid: string | undefined) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'available-sites', uid ?? ''],
    queryFn: () => api.availableSites(uid as string),
    enabled: useEnabled() && !!uid,
    // 409 is a vendor verdict ("could not list sites"), not a transient failure.
    retry: false,
  })
}

export function useManagedSites(uid: string | undefined) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'managed-sites', uid ?? ''],
    queryFn: () => api.managedSites(uid as string),
    enabled: useEnabled() && !!uid,
  })
}

export function useLocationBindings(uid: string | undefined) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'location-bindings', uid ?? ''],
    queryFn: () => api.locationBindings(uid as string),
    enabled: useEnabled() && !!uid,
  })
}

export function useSyncRuns(system?: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'sync-runs', system ?? ''],
    queryFn: () => api.syncRuns(system),
    enabled: useEnabled(),
  })
}

export function useSystemIntegrations(uid: string | undefined) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'system-integrations', uid ?? ''],
    queryFn: () => api.systemIntegrations(uid as string),
    enabled: useEnabled() && !!uid,
  })
}

/** The provider matrix (registry-global — what each provider supports:
 *  capabilities, secrets spec, sync kinds). Long staleTime: it only
 *  changes on a deploy. */
export function useProviderCapabilities() {
  return useQuery({
    queryKey: ['provider-capabilities'],
    queryFn: api.providerCapabilities,
    staleTime: 5 * 60_000,
  })
}

/** The resolved routing table (capability → winning integration + why). */
export function useSystemCapabilities(uid: string | undefined) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'system-capabilities', uid ?? ''],
    queryFn: () => api.systemCapabilities(uid as string),
    enabled: useEnabled() && !!uid,
  })
}

/** The mapping board: kind → items with state undecided/skipped/bound.
 *  400 capability_not_supported is a provider verdict (the connector has no
 *  SupportsAdoption), not a transient failure — never retry it. `site`
 *  (a MappingSite.site_key) filters the board to one site of a multi-site
 *  provider; omit it for the unfiltered board (single-site providers). */
export function useMappingBoard(systemUid: string | undefined, site?: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'mapping', systemUid ?? '', site ?? ''],
    queryFn: () => api.mappingBoard(systemUid as string, site),
    enabled: useEnabled() && !!systemUid,
    retry: false,
  })
}

/** Per-kind undecided/skipped/bound counts (same 400 semantics as the board).
 *  `site` filters to one site, same as the board. */
export function useMappingSummary(systemUid: string | undefined, site?: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'mapping-summary', systemUid ?? '', site ?? ''],
    queryFn: () => api.mappingSummary(systemUid as string, site),
    enabled: useEnabled() && !!systemUid,
    retry: false,
  })
}

/** The site's dimension of a system's mapping board: [] for single-site
 *  providers (no switcher, unfiltered board), 400 capability_not_supported
 *  when the connector has no adoption at all — a provider verdict, never
 *  retried (callers treat an error as "no sites"). */
export function useMappingSites(systemUid: string | undefined) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'mapping-sites', systemUid ?? ''],
    queryFn: () => api.mappingSites(systemUid as string),
    enabled: useEnabled() && !!systemUid,
    staleTime: 30_000,
    retry: false,
  })
}

/** Record one mapping decision. The response IS the board item as it now
 *  stands — patch it into the board cache in place (no refetch); everything
 *  else (entity lists a create touched, summary counts) is marked stale and
 *  refetches when next visited. `site` must be the site the workbench is
 *  looking at so the SITE-SCOPED board cache is the one patched. Exception:
 *  a decide with `fields.series: true` (rotating door-code series) folds the
 *  sibling period rows backend-side, so that one case refetches the board
 *  once instead of patching. */
export function useMappingDecide(systemUid: string, site?: string) {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: MappingDecideBody) => api.mappingDecide(systemUid, body),
    onSuccess: (item, body) => {
      // A rotating-series door_code create folds the whole rotation FAMILY
      // backend-side (sibling quarters bind too), but the response carries
      // only the decided row's item — the siblings would stay visually
      // undecided. One targeted refetch of the site-scoped board flips them
      // all (exact key: never other sites/systems, no storm); everything
      // else keeps the surgical patch.
      if (body.fields?.series === true) {
        void queryClient.invalidateQueries({ queryKey: [scope, 'mapping', systemUid, site ?? ''], exact: true })
      } else {
        queryClient.setQueryData<MappingBoard>([scope, 'mapping', systemUid, site ?? ''], (board) => {
          if (!board) return board
          const rows = board[item.kind] ?? []
          const exists = rows.some((row) => row.vendor_uid === item.vendor_uid)
          return {
            ...board,
            [item.kind]: exists
              ? rows.map((row) => (row.vendor_uid === item.vendor_uid ? item : row))
              : [...rows, item],
          }
        })
      }
      void queryClient.invalidateQueries({ queryKey: [scope], refetchType: 'none' })
      void queryClient.invalidateQueries({ queryKey: [scope, 'mapping-summary'] })
      void queryClient.invalidateQueries({ queryKey: [scope, 'mapping-sites'] })
    },
  })
}

/** One row that failed during a bulk mapping create. */
export interface MappingBulkFailure {
  vendor_uid: string
  display: string
  detail: string
}

/** Rows per decide-bulk call: the whole selection in ONE call for any
 *  realistic selection (the backend items cap is 500) — chunking only
 *  kicks in past this size, so the progress log still moves on a
 *  thousand-row import instead of sitting on one silent POST. */
const BULK_CHUNK = 250

/** One plan of a bulk quick-decide run: the row plus its 100%-hint decide
 *  body (null = no 100% match — logged as skipped, never fired). */
export interface QuickDecidePlan {
  item: MappingItem
  body: MappingDecideBody | null
  /** Past-tense phrase for the run log, e.g. "merged into X". */
  verb: string
  /** fields.series === true create — folds sibling rows backend-side. */
  foldsSiblings: boolean
}

/** Rows per decide-bulk ITEMS-mode call: same one-call posture as
 *  BULK_CHUNK (backend cap 500). The old 5-row chunks existed to keep
 *  the log flushing — but every chunk costs a fixed ~1s of request +
 *  board-rebuild overhead, which made big selections feel slower than
 *  one-and-one. One big call; the in-flight line covers the wait. */
const QUICK_BULK_CHUNK = 250

/** Bulk QUICK-DECIDE runner ("Decide 100% matches"): decide-bulk
 *  ITEMS-mode POSTs — the whole selection in ONE call up to
 *  QUICK_BULK_CHUNK rows, per-row targets/fields inside
 *  (per-row errors never abort the batch). No-match rows never fire — the
 *  '–' skipped lines are client-side. Successful rows patch the
 *  site-scoped board surgically from the result's `item` (same shape as
 *  the single-decide patch); if ANY plan folds siblings (rotating-series
 *  create) the board gets ONE targeted refetch at the end instead.
 *  Summary/sites/scope invalidations mirror the bulk create runner. */
export function useMappingBulkQuickDecide(systemUid: string, site?: string) {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      plans,
      onChunk,
      onProgress,
    }: {
      plans: QuickDecidePlan[]
      onChunk?: (lines: string[], done: number, total: number) => void
      /** Live in-flight line ("working on rows 6–10 of 204"; null at the
       *  end) — the modal shows it while a chunk POST is out. */
      onProgress?: (text: string | null) => void
    }): Promise<MappingBulkFailure[]> => {
      const failures: MappingBulkFailure[] = []
      let folds = false
      for (let start = 0; start < plans.length; start += QUICK_BULK_CHUNK) {
        const chunk = plans.slice(start, start + QUICK_BULK_CHUNK)
        onProgress?.(
          `working on rows ${start + 1}–${Math.min(start + QUICK_BULK_CHUNK, plans.length)} of ${plans.length}`,
        )
        const fireable = chunk.filter((p) => p.body !== null)
        // vendor_uid → per-row outcome, from the one batch POST.
        const results = new Map<string, { ok: boolean; error: string | null; item: MappingItem | null }>()
        if (fireable.length > 0) {
          try {
            const response = await api.mappingDecideBulk(systemUid, {
              kind: chunk[0].item.kind,
              items: fireable.map((p) => ({
                vendor_uid: p.item.vendor_uid,
                action: (p.body as MappingDecideBody).action,
                target: (p.body as MappingDecideBody).target ?? null,
                fields: (p.body as MappingDecideBody).fields ?? null,
              })),
            })
            for (const r of response.results) {
              results.set(r.vendor_uid, { ok: r.ok, error: r.detail || r.error || null, item: r.item ?? null })
            }
          } catch (e) {
            // The batch call itself failed (network/5xx) — mark every fired
            // row of the chunk failed with the shared detail; later chunks
            // still run.
            const detail = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'unknown error'
            for (const p of fireable) results.set(p.item.vendor_uid, { ok: false, error: detail, item: null })
          }
        }
        const lines: string[] = []
        for (const p of chunk) {
          if (p.body === null) {
            lines.push(`– ${p.item.display} — no 100% match, skipped`)
            continue
          }
          const r = results.get(p.item.vendor_uid)
          if (r?.ok) {
            lines.push(`✓ ${p.item.display} — ${p.verb}`)
            if (p.foldsSiblings) folds = true
            if (r.item) {
              // Same surgical patch useMappingDecide applies per row.
              const item = r.item
              queryClient.setQueryData<MappingBoard>([scope, 'mapping', systemUid, site ?? ''], (board) => {
                if (!board) return board
                const rows = board[item.kind] ?? []
                const exists = rows.some((row) => row.vendor_uid === item.vendor_uid)
                return {
                  ...board,
                  [item.kind]: exists
                    ? rows.map((row) => (row.vendor_uid === item.vendor_uid ? item : row))
                    : [...rows, item],
                }
              })
            } else {
              // A success without the board item can't be patched — fold it
              // into the end-of-run refetch instead.
              folds = true
            }
          } else {
            const detail = r?.error ?? 'unknown error'
            lines.push(`✗ ${p.item.display} — ${detail}`)
            failures.push({ vendor_uid: p.item.vendor_uid, display: p.item.display, detail })
          }
        }
        onChunk?.(lines, Math.min(start + QUICK_BULK_CHUNK, plans.length), plans.length)
      }
      // A series create folded sibling rows the patches can't see — one
      // exact-key refetch covers them all.
      if (folds) {
        void queryClient.invalidateQueries({ queryKey: [scope, 'mapping', systemUid, site ?? ''], exact: true })
      }
      onProgress?.(null)
      return failures
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [scope], refetchType: 'none' })
      void queryClient.invalidateQueries({ queryKey: [scope, 'mapping-summary'] })
      void queryClient.invalidateQueries({ queryKey: [scope, 'mapping-sites'] })
    },
  })
}

/** Bulk-create runner for the mapping workbench: chunked decide-bulk POSTs
 *  (the backend applies the create per row, suppresses the per-row ws
 *  envelopes, and reports per-row outcomes — a poison row never aborts the
 *  batch). `onChunk` fires after each chunk with what landed, so the modal
 *  can log "created 25 access groups (50/192)" as the data comes in. The
 *  board is patched from the outcomes (no refetch); every other query is
 *  just marked stale so lists refetch when next visited. `site` must be the
 *  site the workbench is looking at (the site-scoped board cache is patched). */
export function useMappingBulkCreate(systemUid: string, provider: string, site?: string) {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      items,
      onChunk,
    }: {
      items: MappingItem[]
      onChunk?: (created: number, failed: MappingBulkFailure[], done: number, total: number) => void
    }): Promise<MappingBulkFailure[]> => {
      const byUid = new Map(items.map((i) => [i.vendor_uid, i]))
      const kind = items[0]?.kind ?? ''
      const failures: MappingBulkFailure[] = []
      // Person bulk create bundles the holder's cards exactly like the
      // one-click path (defaultCreateFields) — without it 868 cards land
      // holder-less (prod regression 2026-08-11).
      const fields =
        kind === 'person' && credentialBundleProviders.has(provider) ? { include_credentials: true } : undefined
      for (let start = 0; start < items.length; start += BULK_CHUNK) {
        const chunk = items.slice(start, start + BULK_CHUNK)
        const response = await api.mappingDecideBulk(systemUid, {
          kind,
          action: 'create',
          vendor_uids: chunk.map((i) => i.vendor_uid),
          fields,
        })
        // Patch the board from the outcomes — no refetch wave.
        const outcomes = new Map(
          response.results.filter((r) => r.ok).map((r) => [r.vendor_uid, r.outcome ?? null]),
        )
        queryClient.setQueryData<MappingBoard>([scope, 'mapping', systemUid, site ?? ''], (board) => {
          if (!board) return board
          const rows = board[kind] ?? []
          return {
            ...board,
            [kind]: rows.map((row) =>
              outcomes.has(row.vendor_uid)
                ? { ...row, state: 'bound' as const, outcome: outcomes.get(row.vendor_uid) ?? null }
                : row,
            ),
          }
        })
        const chunkFailures = response.results
          .filter((r) => !r.ok)
          .map((r) => ({
            vendor_uid: r.vendor_uid,
            display: byUid.get(r.vendor_uid)?.display ?? r.vendor_uid,
            detail: r.detail || r.error || 'unknown error',
          }))
        failures.push(...chunkFailures)
        onChunk?.(
          response.results.length - chunkFailures.length,
          chunkFailures,
          Math.min(start + BULK_CHUNK, items.length),
          items.length,
        )
      }
      return failures
    },
    onSettled: () => {
      // Derived counts + any list the created rows belong to: stale, not
      // stormed — they refetch on next visit.
      void queryClient.invalidateQueries({ queryKey: [scope], refetchType: 'none' })
      void queryClient.invalidateQueries({ queryKey: [scope, 'mapping-summary'] })
      void queryClient.invalidateQueries({ queryKey: [scope, 'mapping-sites'] })
    },
  })
}

/** The location types the split-on tool offers per level (backend
 *  LocationType values, the physical-building subset). Level 1 defaults to
 *  `address`, the leaf to `apartment`. */
export const SPLIT_LOCATION_TYPES = ['city', 'address', 'floor', 'apartment', 'room', 'other'] as const

/** Split-on v2 always splits on ANY of these; the operator can add one
 *  custom separator on top. */
export const SPLIT_DEFAULT_SEPARATORS = ['–', '-', '/']

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** Split one candidate's display name into trimmed, non-empty path segments,
 *  splitting on ANY of the default separators (`–`, `-`, `/`) plus an
 *  optional custom one (`A – B` → ['A', 'B']). */
export function splitOnSegments(display: string, customSeparator = ''): string[] {
  const separators = [...SPLIT_DEFAULT_SEPARATORS]
  if (customSeparator !== '' && !separators.includes(customSeparator)) {
    separators.push(customSeparator)
  }
  const pattern = new RegExp(separators.map(escapeRegExp).join('|'))
  return display
    .split(pattern)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

/** Human labels for the split-on per-row taxonomy errors (a bare
 *  'already_adopted' means nothing to an operator). */
const SPLIT_ON_ERROR_LABELS: Record<string, string> = {
  already_adopted: 'already bound',
  unknown_candidate: 'no longer a candidate (re-synced away?)',
  merge_target_not_found: 'target not found',
  kind_mismatch: 'target kind mismatch',
  writes_not_allowed: 'system is read-only',
  invalid_vendor_data: 'empty split path',
  capability_not_supported: 'not supported by this system',
  internal_error: 'internal error',
}

/** Rows per split-on call: one POST per chunk (never one per row), so a
 *  200-row import is a handful of requests and progress can tick between
 *  them. */
const SPLIT_ON_CHUNK = 25

/** Insert-or-find each chain node in the cached location tree (immutable
 *  copies along the path; matches by unique_id first, then name). */
function upsertLocationChain(nodes: LocationNode[], chain: MappingSplitOnNode[]): LocationNode[] {
  if (chain.length === 0) return nodes
  const [head, ...rest] = chain
  const idx = nodes.findIndex((n) => n.unique_id === head.unique_id || n.name === head.name)
  if (idx >= 0) {
    const node = nodes[idx]
    const updated = { ...node, children: upsertLocationChain(node.children, rest) }
    return [...nodes.slice(0, idx), updated, ...nodes.slice(idx + 1)]
  }
  const fresh: LocationNode = {
    unique_id: head.unique_id,
    name: head.name,
    type: head.type ?? 'other',
    external_ref: '',
    children: upsertLocationChain([], rest),
  }
  return [...nodes, fresh]
}

/** Split-on v2 runner for the mapping wizard's Groups step. Chunked
 *  split-on POSTs carry the per-row split paths EXACTLY as previewed (the
 *  server executes, never re-splits); each response carries the
 *  found-or-created location + group chains and the leaf-group outcome, so
 *  the console PATCHES its own caches (board rows flip to bound, tree and
 *  group lists upsert) instead of refetching — one response per chunk,
 *  zero invalidation storms. The server suppresses per-object ws envelopes
 *  for the batch for the same reason. `site` must be the site the workbench
 *  is looking at (the site-scoped board cache is patched). */
export function useMappingSplitOn(systemUid: string, site?: string) {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      items,
      customSeparator = '',
      levelTypes,
      manage = false,
      onProgress,
    }: {
      items: MappingItem[]
      /** Extra separator on top of the defaults; '' = defaults only. */
      customSeparator?: string
      /** Location type per tree level, index 0 = level 1. A leaf of a
       *  shallower row takes the last configured type. */
      levelTypes: string[]
      /** Managed binding (vendor write-back follows the group path) — only
       *  honored on systems whose config allows writes. */
      manage?: boolean
        onProgress?: (done: number, total: number, chunkFailures: MappingBulkFailure[]) => void
    }): Promise<MappingBulkFailure[]> => {
      const typeFor = (i: number, depth: number): string => {
        if (i === depth - 1) return levelTypes[Math.min(depth, levelTypes.length) - 1] ?? 'apartment'
        return levelTypes[i] ?? 'floor'
      }
      const byUid = new Map(items.map((i) => [i.vendor_uid, i]))
      const kind = items[0]?.kind ?? 'group'
      const payloads = items.map((item) => {
        const segments = splitOnSegments(item.display, customSeparator)
        const path = segments.length >= 2 ? segments : [item.display.trim()]
        return {
          vendor_uid: item.vendor_uid,
          segments: path.map((name, i) => ({
            name,
            location_type: path.length >= 2 ? typeFor(i, path.length) : null,
          })),
        }
      })

      const failures: MappingBulkFailure[] = []
      for (let start = 0; start < payloads.length; start += SPLIT_ON_CHUNK) {
        const chunk = payloads.slice(start, start + SPLIT_ON_CHUNK)
        const response = await api.mappingSplitOn(systemUid, { kind, manage, items: chunk })

        // Patch the board: succeeded rows flip to bound with their leaf group.
        const outcomes = new Map(
          response.results.filter((r) => r.ok).map((r) => [r.vendor_uid, r.outcome ?? null]),
        )
        queryClient.setQueryData<MappingBoard>([scope, 'mapping', systemUid, site ?? ''], (board) => {
          if (!board) return board
          const rows = board[kind] ?? []
          return {
            ...board,
            [kind]: rows.map((row) =>
              outcomes.has(row.vendor_uid)
                ? { ...row, state: 'bound' as const, outcome: outcomes.get(row.vendor_uid) ?? null }
                : row,
            ),
          }
        })

        // Patch the location tree + group list from the returned chains
        // (shared prefixes collapse inside the walk).
        queryClient.setQueryData<LocationNode[]>([scope, 'locations', 'tree'], (tree) => {
          if (!tree) return tree
          let next = tree
          for (const r of response.results) {
            if (r.ok && r.locations && r.locations.length > 0) next = upsertLocationChain(next, r.locations)
          }
          return next
        })
        queryClient.setQueryData<Group[]>([scope, 'groups', ''], (groups) => {
          if (!groups) return groups
          const have = new Set(groups.map((g) => g.unique_id))
          const additions: Group[] = []
          for (const r of response.results) {
            if (!r.ok) continue
            for (const node of r.groups ?? []) {
              if (have.has(node.unique_id)) continue
              have.add(node.unique_id)
              additions.push({
                unique_id: node.unique_id,
                name: node.name,
                kind: 'organisation',
                external_ref: '',
                parent: node.parent,
                people_count: 0,
                inherits_grants: true,
                systems: [],
              })
            }
          }
          return additions.length > 0 ? [...groups, ...additions] : groups
        })

        const chunkFailures: MappingBulkFailure[] = []
        for (const r of response.results) {
          if (r.ok) continue
          chunkFailures.push({
            vendor_uid: r.vendor_uid,
            display: byUid.get(r.vendor_uid)?.display ?? r.vendor_uid,
            detail: SPLIT_ON_ERROR_LABELS[r.error ?? ''] ?? r.error ?? 'unknown error',
          })
        }
        failures.push(...chunkFailures)
        onProgress?.(Math.min(start + SPLIT_ON_CHUNK, payloads.length), payloads.length, chunkFailures)
      }
      // One cheap refresh for the derived counts (summary chips / dashboard
      // CTA) — the board itself is already patched.
      void queryClient.invalidateQueries({ queryKey: [scope, 'mapping-summary'] })
      void queryClient.invalidateQueries({ queryKey: [scope, 'mapping-sites'] })
      return failures
    },
  })
}

// --- Vendor raw-data browser ---

/** Table list with live counts (one entry per mirrored vendor model). Admin-only API. */
export function useVendorDataTables(systemUid: string | undefined, enabled = true) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'vendor-data', systemUid ?? ''],
    queryFn: () => api.vendorDataTables(systemUid as string),
    enabled: useEnabled() && enabled && !!systemUid,
  })
}

/** Cursor-paged rows of one vendor table; `table` undefined = not loaded. */
export function useInfiniteVendorDataRows(systemUid: string | undefined, table: string | undefined) {
  const scope = useScope()
  return useInfiniteQuery({
    queryKey: [scope, 'vendor-data', systemUid ?? '', table ?? ''],
    queryFn: ({ pageParam }) => api.vendorDataRows(systemUid as string, table as string, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: useEnabled() && !!systemUid && !!table,
  })
}

// --- Axema connector ---

/** AxDoor mirror rows for one Axema system (each row carries the bound DCIP
 *  controller MAC). Mount only for provider 'axema' — the route 404s for
 *  other providers' systems. */
export function useAxemaDoors(systemUid: string | undefined, enabled = true) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'axema-doors', systemUid ?? ''],
    queryFn: () => api.axemaDoors(systemUid as string),
    enabled: useEnabled() && enabled && !!systemUid,
  })
}

/** Pending DCIP controllers (the bind modal's pick-list). Operator+ only —
 *  a 403 is a role verdict, not a transient failure, so never retried (the
 *  caller falls back to manual MAC entry on any error). */
export function useAxemaPendingControllers(systemUid: string | undefined, enabled = true) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'axema-pending-controllers', systemUid ?? ''],
    queryFn: () => api.axemaPendingControllers(systemUid as string),
    enabled: useEnabled() && enabled && !!systemUid,
    retry: false,
  })
}

export function useDevices(systemUid?: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'devices', systemUid ?? ''],
    queryFn: () => api.devices(systemUid),
    enabled: useEnabled(),
  })
}

export function useDevice(uid: string | undefined) {
  const scope = useScope()
  return useQuery({
    queryKey: [scope, 'device', uid ?? ''],
    queryFn: () => api.device(uid as string),
    enabled: useEnabled() && !!uid,
  })
}

/** Update a device's Location/Door placement. Invalidates device list/detail. */
export function useUpdateDevice() {
  const scope = useScope()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ uid, body }: { uid: string; body: DeviceUpdateBody }) => api.updateDevice(uid, body),
    onSuccess: (_, { uid }) => {
      queryClient.invalidateQueries({ queryKey: [scope, 'devices'] })
      queryClient.invalidateQueries({ queryKey: [scope, 'device', uid] })
    },
  })
}

export function useEdgeGateways() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'edge-gateways'], queryFn: api.edgeGateways, enabled: useEnabled() })
}

export function useEdgeInstances() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'edge-instances'], queryFn: api.edgeInstances, enabled: useEnabled() })
}

export function useInfiniteEdgeGatewayLogs(uid: string | undefined) {
  const scope = useScope()
  return useInfiniteQuery({
    queryKey: [scope, 'edge-gateway-logs', uid ?? ''],
    queryFn: ({ pageParam }) => api.edgeGatewayLogs(uid as string, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: useEnabled() && !!uid,
  })
}

/** Release notes + the API's running version. Global content (not
 *  customer data) that only changes on a deploy — fetched once per session. */
/** Public /healthz -- used where there is no session yet (login page) to show
 *  the running API version. Never retried: an unreachable API just shows no
 *  version. */
export function useApiHealth() {
  return useQuery({ queryKey: ['healthz'], queryFn: api.health, staleTime: 5 * 60_000, retry: false })
}

export function useChangelog() {
  return useQuery({ queryKey: ['changelog'], queryFn: api.changelog, enabled: useEnabled(), staleTime: Infinity })
}

export interface AuditFilters {
  category?: string
  action?: string
  target?: string
  actor?: string
  since?: string
}

export function useAudit(filters: AuditFilters = {}) {
  const scope = useScope()
  return useInfiniteQuery({
    queryKey: [scope, 'audit', filters],
    queryFn: ({ pageParam }) => api.audit({ ...filters, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: useEnabled(),
  })
}

export function useProfile() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'profile'], queryFn: api.profile, enabled: useEnabled() })
}

export function useProfileCustomers() {
  return useQuery({
    queryKey: ['profile-customers'],
    queryFn: api.profileCustomers,
    enabled: useEnabled(),
  })
}

/**
 * Every customer the principal holds a grant on (identity-scoped, so the key
 * deliberately carries no customer scope — the list survives a switch).
 */
export function useCustomers() {
  const identityValid = useAuthStore((s) => !isExpired(s.identityToken))
  return useQuery({ queryKey: ['customers'], queryFn: api.customers, enabled: identityValid })
}

export function useProfileActivity() {
  const scope = useScope()
  return useInfiniteQuery({
    queryKey: [scope, 'profile-activity'],
    queryFn: ({ pageParam }) => api.profileActivity(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: useEnabled(),
  })
}

export function useTrash() {
  const scope = useScope()
  return useQuery({ queryKey: [scope, 'trash'], queryFn: api.trash, enabled: useEnabled() })
}

export function useRestoreTrash() {
  const queryClient = useQueryClient()
  const scope = useScope()
  return useMutation({
    mutationFn: api.restoreTrash,
    onSuccess: () => {
      // The restored row reappears in its normal list/detail views; invalidate
      // the customer scope so every cached entity list refetches on next visit.
      void queryClient.invalidateQueries({ queryKey: [scope] })
      void queryClient.invalidateQueries({ queryKey: [scope, 'trash'] })
    },
  })
}

export function usePurgeTrash() {
  const queryClient = useQueryClient()
  const scope = useScope()
  return useMutation({
    mutationFn: api.purgeTrash,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope, 'trash'] })
    },
  })
}
